import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["as"],
  _excluded2 = ["icon", "iconScale", "src", "link", "children"];
import "core-js/modules/es.string.link.js";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { fillParent } from 'styles/mixins';
import { pulse } from 'styles/animations';
import { FILE_STATUS_VALUES } from 'actions/file';
import Box from 'components/Box';
import Button from 'components/Button';
import CardImage from 'components/CardImage';
import Flex from 'components/Flex';
import Icon from 'components/Icon';
import ActionButtonMenu from 'components/ActionButtonMenu';
import Text from 'components/Text';
export var getCardTitleId = function getCardTitleId(id) {
  return "".concat(id, "-title");
};
export var getCardSubtitleId = function getCardSubtitleId(id) {
  return "".concat(id, "-subtitle");
};
export var getCardDescriptionId = function getCardDescriptionId(id) {
  return "".concat(id, "-description");
};
export var getCardMenuId = function getCardMenuId(id) {
  return "".concat(id, "-card-menu");
};
export var StackableCardContent = styled(Flex).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'stackBelow';
  }
}).withConfig({
  displayName: "misc__StackableCardContent",
  componentId: "sc-u8gpxp-0"
})(["position:relative;flex-direction:row;align-items:flex-start;flex:1 1 auto;", ""], function (p) {
  return p.stackBelow && css(["@media (max-width:", "){flex-direction:column;align-items:stretch;}"], p.theme.globals.breakpoints[p.stackBelow]);
});
export var IconsAndMenuBox = styled(Box).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'stackedBelow';
  }
}).withConfig({
  displayName: "misc__IconsAndMenuBox",
  componentId: "sc-u8gpxp-1"
})(["z-index:", ";align-self:flex-end;", ""], function (p) {
  return p.theme.globals.z.raised;
}, function (p) {
  return p.stackedBelow && css(["@media (min-width:", "){align-self:flex-start;}"], p.theme.globals.breakpoints[p.stackedBelow]);
});
export function Subtitle(props) {
  return /*#__PURE__*/React.createElement(Text, _extends({
    as: "div",
    dimmed: true,
    size: "s"
  }, props));
}
export function TypeIcon(props) {
  return /*#__PURE__*/React.createElement(Icon, _extends({
    vAlign: true,
    dimmed: true,
    scale: 0,
    mr: "xxs"
  }, props));
}
export var ContentCardButton = function ContentCardButton(_ref) {
  var as = _ref.as,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var Component = as || 'button';
  return /*#__PURE__*/React.createElement(Button, _extends({
    size: "small",
    mt: -4
  }, otherProps, {
    as: Component,
    position: "relative",
    z: "raised"
  }));
};
var PulsingIcon = styled(Icon).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'withAnimation';
  }
}).withConfig({
  displayName: "misc__PulsingIcon",
  componentId: "sc-u8gpxp-2"
})(["", ""], function (p) {
  return p.withAnimation && css(["animation:", " 1s ease-in alternate infinite;"], pulse);
});
export function ProcessingStatus(_ref2) {
  var status = _ref2.status;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  return /*#__PURE__*/React.createElement(Text
  // override dimmed text from parent component
  , {
    dimmed: status === FILE_STATUS_VALUES.PROCESSING ? false : undefined,
    textColor: status === FILE_STATUS_VALUES.INVALID ? 'negative' : undefined
  }, /*#__PURE__*/React.createElement(PulsingIcon, {
    vAlign: true,
    name: "dot",
    withAnimation: status === FILE_STATUS_VALUES.PROCESSING,
    mr: "xxxs"
  }), t("content_processing_status_".concat(status)));
}
var PlayIcon = styled(Icon).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'scale';
  }
}).withConfig({
  displayName: "misc__PlayIcon",
  componentId: "sc-u8gpxp-3"
})(["filter:drop-shadow(0 0 8px rgba(0,0,0,0.75));font-size:", "rem;@media (min-width:", "){font-size:", "rem;}"], function (p) {
  return p.scale * 2;
}, function (p) {
  return p.theme.globals.breakpoints.m;
}, function (p) {
  return p.scale * 2.666;
});
var ImageLink = styled.span.attrs({
  'aria-hidden': 'true',
  role: 'presentation',
  tabIndex: -1
}).withConfig({
  displayName: "misc__ImageLink",
  componentId: "sc-u8gpxp-4"
})(["", " color:", ";"], fillParent, function (p) {
  return p.theme.text;
});
export function Image(_ref3) {
  var icon = _ref3.icon,
    _ref3$iconScale = _ref3.iconScale,
    iconScale = _ref3$iconScale === void 0 ? 1.5 : _ref3$iconScale,
    src = _ref3.src,
    link = _ref3.link,
    children = _ref3.children,
    otherProps = _objectWithoutProperties(_ref3, _excluded2);
  var isVideo = icon === 'asset-video';
  var hasImage = Boolean(src);
  return /*#__PURE__*/React.createElement(CardImage, _extends({
    colorBlock: icon,
    aspectRatio: 5 / 12,
    maxHeight: 150,
    fallbackContent: /*#__PURE__*/React.createElement(Icon, {
      dimmed: true,
      name: icon,
      scale: iconScale
    }),
    src: src
  }, otherProps), isVideo && hasImage && /*#__PURE__*/React.createElement(PlayIcon, {
    scale: iconScale,
    dimmed: true,
    name: "play"
  }), /*#__PURE__*/React.createElement(ImageLink, {
    to: link,
    as: link ? Link : 'span'
  }, children));
}
export var DescriptionIcon = styled(Icon).attrs({
  name: 'info'
}).withConfig({
  displayName: "misc__DescriptionIcon",
  componentId: "sc-u8gpxp-5"
})(["vertical-align:top;margin:", ";filter:drop-shadow(0 0 8px rgba(0,0,0,0.75));@media (max-width:", "){display:none;}"], function (p) {
  return p.theme.globals.spacing.xs;
}, function (p) {
  return p.theme.globals.breakpoints.s;
});
export function ActionMenu(_ref4) {
  var items = _ref4.items,
    id = _ref4.id,
    titleId = _ref4.titleId;
  // Negative margins on the wrapper are needed to align
  // the menu button with the badges next to it
  return /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    z: "raised",
    my: -9,
    mr: "-xs"
  }, /*#__PURE__*/React.createElement(ActionButtonMenu, {
    items: items,
    id: id,
    titleId: titleId
  }));
}